import { Table, UpdateSpec } from 'dexie';

import { AppDatabase, DatabaseService } from '../database.service';
import {
  RecordNotFoundError,
  StorageQuotaExceededError,
  isQuotaError,
} from '../errors/offline.errors';
import { EntityDescriptor } from '../models/sync-registry';
import {
  SyncOperation,
  SyncOperationType,
  SyncableEntity,
} from '../models/sync.model';

/** Fields the repository owns; callers supply everything else. */
export type NewRecord<T extends SyncableEntity> = Omit<
  T,
  'localId' | 'uuid' | 'serverId' | 'version' | 'createdAt' | 'updatedAt' | 'syncStatus' | 'deletedAt'
> &
  Partial<Pick<T, 'serverId' | 'version'>>;

/** A patch. Sync bookkeeping cannot be set by callers. */
export type RecordPatch<T extends SyncableEntity> = Partial<
  Omit<T, 'localId' | 'uuid' | 'createdAt' | 'syncStatus' | 'version'>
>;

/**
 * Identity for records created on this device.
 *
 * crypto.randomUUID needs a secure context; the fallback keeps the app working
 * on a plain-HTTP LAN deployment. Collision risk is irrelevant at this scale,
 * and the server treats the value as an opaque unique string either way.
 */
export function newUuid(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `uuid-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * CRUD over one Dexie table, with the queue kept in step.
 *
 * The invariant this class exists to hold: **a record is never mutated without
 * its sync operation being written in the same IndexedDB transaction.** If the
 * two were separate writes, a tab closed between them would either lose the
 * change (record written, nothing queued) or replay a change that was never
 * applied. Dexie transactions are what make that atomic, so every mutator here
 * routes through `db.transaction('rw', table, syncQueue, ...)`.
 *
 * Reads never touch the network. That is the whole point: the UI asks the
 * repository, gets local data, and behaves identically online and offline.
 */
export abstract class BaseRepository<T extends SyncableEntity> {
  protected constructor(
    protected readonly databaseService: DatabaseService,
    protected readonly descriptor: EntityDescriptor,
  ) {}

  /** The Dexie table this repository owns. */
  protected async table(): Promise<Table<T, number>> {
    const db = await this.databaseService.ready();
    return db.table<T, number>(this.descriptor.table);
  }

  protected async db(): Promise<AppDatabase> {
    return this.databaseService.ready();
  }

  // ── Reads ────────────────────────────────────────────────────────────────

  /** Every live record. Tombstoned rows are excluded. */
  async getAll(): Promise<T[]> {
    const table = await this.table();
    const rows = await table.toArray();
    return rows.filter((r) => !r.deletedAt);
  }

  async getByLocalId(localId: number): Promise<T | undefined> {
    const table = await this.table();
    return table.get(localId);
  }

  async getByUuid(uuid: string): Promise<T | undefined> {
    const table = await this.table();
    return table.where('uuid').equals(uuid).first();
  }

  /** Look up by the server's primary key, which is how the API refers to it. */
  async getByServerId(serverId: number): Promise<T | undefined> {
    const table = await this.table();
    return table.where('serverId').equals(serverId).first();
  }

  async countPending(): Promise<number> {
    const table = await this.table();
    return table.where('syncStatus').anyOf('pending', 'failed', 'syncing').count();
  }

  /** Records the user must decide about before they can sync. */
  async getConflicted(): Promise<T[]> {
    const table = await this.table();
    return table.where('syncStatus').equals('conflict').toArray();
  }

  // ── Writes ───────────────────────────────────────────────────────────────

  /**
   * Create locally and queue the create.
   *
   * Returns immediately with the stored record - the caller does not wait for,
   * and does not care about, the network.
   */
  async create(input: NewRecord<T>): Promise<T> {
    const now = Date.now();
    const record = {
      ...(input as object),
      uuid: newUuid(),
      serverId: (input as Partial<T>).serverId ?? null,
      version: (input as Partial<T>).version ?? 0,
      createdAt: now,
      updatedAt: now,
      syncStatus: 'pending',
      deletedAt: null,
    } as unknown as T;

    return this.runWrite(async (db, table) => {
      const localId = await table.add(record);
      const stored = { ...record, localId } as T;
      if (this.descriptor.pushable) {
        await this.enqueue(db, 'create', stored);
      } else {
        // Pull-only entity: nothing to send, so it is already in step with the
        // server as far as this client is concerned.
        await table.update(localId, {
          syncStatus: 'synced',
        } as unknown as UpdateSpec<T>);
        stored.syncStatus = 'synced';
      }
      return stored;
    });
  }

  /**
   * Patch locally and queue the update.
   *
   * A record already in `conflict` is refused: overwriting it would discard the
   * very divergence the user still has to rule on. Resolve it first.
   */
  async update(localId: number, patch: RecordPatch<T>): Promise<T> {
    return this.runWrite(async (db, table) => {
      const existing = await table.get(localId);
      if (!existing) {
        throw new RecordNotFoundError(
          `${this.descriptor.name}#${localId} does not exist locally.`,
        );
      }
      if (existing.syncStatus === 'conflict') {
        throw new Error(
          `${this.descriptor.name}#${localId} is in conflict and must be resolved before further edits.`,
        );
      }

      const updated = {
        ...existing,
        ...(patch as object),
        updatedAt: Date.now(),
        syncStatus: this.descriptor.pushable ? 'pending' : 'synced',
      } as T;

      await table.put(updated);
      if (this.descriptor.pushable) {
        await this.enqueue(db, 'update', updated);
      }
      return updated;
    });
  }

  /**
   * Soft-delete and queue the delete.
   *
   * The row is tombstoned rather than removed so the queued operation still has
   * a record to reference, and so a delete made offline survives a reload. It is
   * hard-deleted once the server confirms (SyncService.applyResult).
   */
  async delete(localId: number): Promise<void> {
    await this.runWrite(async (db, table) => {
      const existing = await table.get(localId);
      if (!existing) return;

      // Never synced: nothing on the server to delete, so drop it outright
      // along with any operations still queued for it.
      if (existing.serverId === null) {
        await db.syncQueue
          .where('[entity+localId]')
          .equals([this.descriptor.name, localId])
          .delete();
        await table.delete(localId);
        return;
      }

      const tombstoned = {
        ...existing,
        deletedAt: Date.now(),
        updatedAt: Date.now(),
        syncStatus: 'pending',
      } as T;
      await table.put(tombstoned);
      if (this.descriptor.pushable) {
        await this.enqueue(db, 'delete', tombstoned);
      }
    });
  }

  /**
   * Write server-authoritative rows into the local store.
   *
   * Used by the initial prefetch and by every pull. A row carrying unsynced
   * local work is left alone - SyncService decides whether that is a conflict;
   * silently replacing it here would destroy the user's offline edits.
   */
  async upsertFromServer(rows: T[]): Promise<{ written: number; skipped: number }> {
    if (rows.length === 0) return { written: 0, skipped: 0 };

    return this.runWrite(async (_db, table) => {
      let written = 0;
      let skipped = 0;

      for (const row of rows) {
        const existing = row.uuid
          ? await table.where('uuid').equals(row.uuid).first()
          : row.serverId !== null
            ? await table.where('serverId').equals(row.serverId).first()
            : undefined;

        if (existing && existing.syncStatus !== 'synced') {
          skipped++;
          continue;
        }

        await table.put({
          ...row,
          localId: existing?.localId,
          syncStatus: 'synced',
        } as T);
        written++;
      }

      return { written, skipped };
    });
  }

  // ── Internals ────────────────────────────────────────────────────────────

  /**
   * Append an operation to the queue. Always called inside the caller's
   * transaction so the record and its operation commit together.
   *
   * Consecutive edits to the same record collapse: if an unsent operation is
   * already queued for it, its payload is replaced rather than a second one
   * added. Auto-save fires on every keystroke, and a queue that grew per
   * keystroke would push hundreds of superseded writes on reconnect.
   */
  private async enqueue(
    db: AppDatabase,
    operation: SyncOperationType,
    record: T,
  ): Promise<void> {
    const now = Date.now();
    const pending = await db.syncQueue
      .where('[entity+localId]')
      .equals([this.descriptor.name, record.localId as number])
      .filter((op) => op.status !== 'syncing')
      .first();

    const payload = operation === 'delete' ? null : this.toPayload(record);

    if (pending) {
      // A queued create followed by edits is still a create - the server has
      // never seen this record, so it must not be downgraded to an update.
      const merged: SyncOperation = {
        ...pending,
        operation: pending.operation === 'create' ? 'create' : operation,
        payload,
        serverId: record.serverId,
        baseVersion: record.version,
        status: 'pending',
        attempts: 0,
        nextAttemptAt: now,
        lastError: null,
        updatedAt: now,
      };
      await db.syncQueue.put(merged);
      return;
    }

    await db.syncQueue.add({
      opId: newUuid(),
      entity: this.descriptor.name,
      operation,
      localId: record.localId as number,
      uuid: record.uuid,
      serverId: record.serverId,
      payload,
      baseVersion: record.version,
      status: 'pending',
      attempts: 0,
      nextAttemptAt: now,
      lastError: null,
      createdAt: now,
      updatedAt: now,
    });
  }

  /**
   * The body sent to the server. Local-only bookkeeping is stripped; the server
   * neither knows nor cares about localId or syncStatus.
   */
  protected toPayload(record: T): Record<string, unknown> {
    const {
      localId: _localId,
      syncStatus: _syncStatus,
      ...rest
    } = record as SyncableEntity as Record<string, unknown> & SyncableEntity;
    void _localId;
    void _syncStatus;
    return rest as Record<string, unknown>;
  }

  /**
   * Run a read-write transaction over this table plus the queue, translating a
   * full-disk failure into a typed error the UI can act on.
   */
  protected async runWrite<R>(
    work: (db: AppDatabase, table: Table<T, number>) => Promise<R>,
  ): Promise<R> {
    const db = await this.db();
    const table = db.table<T, number>(this.descriptor.table);
    try {
      return await db.transaction('rw', table, db.syncQueue, () =>
        work(db, table),
      );
    } catch (error) {
      if (isQuotaError(error)) {
        throw new StorageQuotaExceededError(
          'The browser is out of storage for this site. Sync pending changes or free space before continuing.',
          null,
          null,
          error,
        );
      }
      throw error;
    }
  }
}
